<template>
  <ProjectSection
    id="jarvis"
    title="Jarvis"
    tagline="An on-device voice assistant that never sends a word to the cloud."
    chip="Python · C++ · ONNX Runtime · Ollama"
    accent="#8b5cf6"
  >
    <template #emblem>
      <div class="voice-orb">
        <span class="orb-layer a"></span>
        <span class="orb-layer b"></span>
        <span class="orb-layer c"></span>
      </div>
    </template>

    <!-- Pipeline -->
    <article v-reveal class="card span-12 pipe-card">
      <h3>Fully local. Zero cloud calls.</h3>
      <p>Runs on a Mac mini (M4, 16 GB). In live room tests, spoken commands became lighting scenes and spoken replies, and the TV turned itself down while I talked.</p>
      <div class="pipeline">
        <template v-for="(step, i) in pipeline" :key="step.label">
          <div class="step" :style="{ '--i': i }">
            <span class="step-icon"><font-awesome-icon :icon="['fas', step.icon]" /></span>
            <span class="step-label">{{ step.label }}</span>
            <span class="mono step-tool">{{ step.tool }}</span>
          </div>
          <span v-if="i < pipeline.length - 1" class="link"><span class="pulse" :style="{ animationDelay: i * 0.35 + 's' }"></span></span>
        </template>
      </div>
    </article>

    <!-- AEC -->
    <article v-reveal class="card span-7 aec-card">
      <h3>Hears you over the TV</h3>
      <p>A C bridge to WebRTC AEC3 and a double-talk controller use the TV's own audio as the echo reference for a 4-mic array.</p>
      <div class="waves visual">
        <div v-for="w in waves" :key="w.label" class="wave-row">
          <span class="wave-label">{{ w.label }}</span>
          <svg viewBox="0 0 400 40" preserveAspectRatio="none" class="wave" :class="w.cls">
            <path :d="w.path" />
          </svg>
        </div>
      </div>
    </article>

    <!-- TTS latency -->
    <article v-reveal="80" class="card span-5 tts-card">
      <h3>Replies start 4.5× sooner</h3>
      <p>Cut Piper text-to-speech time to first audio from about 610 ms to 135 ms.</p>
      <div class="lat visual">
        <div class="lat-col">
          <span class="lat-bar before"></span>
          <span class="mono">610 ms</span>
          <small>before</small>
        </div>
        <div class="lat-col">
          <span class="lat-bar after"></span>
          <span class="mono hot">135 ms</span>
          <small>after</small>
        </div>
      </div>
    </article>

    <!-- Benchmarks -->
    <article v-reveal class="card span-12 bench-card">
      <h3>Benchmarked first, then made reliable</h3>
      <p>Each model was tested on recorded room audio. Local LLMs were scored on speed and safety before I shipped a deterministic command grammar.</p>
      <div class="bench">
        <div v-for="row in bench" :key="row.task" class="bench-row" :class="{ shipped: row.shipped }">
          <span class="bench-task">{{ row.task }}</span>
          <span class="bench-model">{{ row.model }}</span>
          <span class="mono bench-result">{{ row.result }}</span>
        </div>
      </div>
    </article>
  </ProjectSection>
</template>

<script>
import ProjectSection from '../ProjectSection.vue';

function wave(amp, freq, noise, seed) {
  let s = seed;
  const pts = [];
  for (let x = 0; x <= 400; x += 4) {
    s = (s * 9301 + 49297) % 233280;
    const n = (s / 233280 - 0.5) * noise;
    const env = Math.sin((x / 400) * Math.PI);
    const y = 20 + (Math.sin(x * freq) * amp + n) * env;
    pts.push(`${x},${y.toFixed(1)}`);
  }
  return 'M' + pts.join(' L');
}

export default {
  name: 'JarvisSection',
  components: { ProjectSection },
  data() {
    return {
      pipeline: [
        { label: 'Wake word', tool: 'openWakeWord', icon: 'ear-listen' },
        { label: 'Is it me?', tool: 'WeSpeaker', icon: 'fingerprint' },
        { label: 'Who spoke', tool: 'diart', icon: 'users' },
        { label: 'Speech to text', tool: 'faster-whisper', icon: 'closed-captioning' },
        { label: 'Think', tool: 'Ollama', icon: 'brain' },
        { label: 'Speak', tool: 'Piper', icon: 'volume-high' },
      ],
      waves: [
        { label: 'TV audio', cls: 'tv', path: wave(10, 0.09, 14, 3) },
        { label: 'Mic input', cls: 'mix', path: wave(15, 0.05, 22, 11) },
        { label: 'After AEC3', cls: 'clean', path: wave(13, 0.035, 3, 5) },
      ],
      bench: [
        { task: 'Wake word', model: 'openWakeWord', result: 'room-tested' },
        { task: 'Speaker verification', model: 'WeSpeaker ResNet34', result: 'room-tested' },
        { task: 'Tool calls', model: 'Qwen2.5-3B', result: '~1.3 s' },
        { task: 'Speech-in first token', model: 'Ultravox', result: '705 ms' },
        { task: 'Shipped', model: 'Deterministic command grammar', result: 'reliable', shipped: true },
      ],
    };
  },
};
</script>

<style scoped>
/* Emblem orb */
.voice-orb {
  position: relative;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #ede9fe, #8b5cf6 45%, #2e1065 100%);
  box-shadow: 0 0 80px rgba(139, 92, 246, 0.55), inset 0 -10px 30px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

.orb-layer {
  position: absolute;
  inset: -20%;
  border-radius: 45%;
  mix-blend-mode: screen;
  animation: swirl 9s linear infinite;
}

.orb-layer.a { background: radial-gradient(circle at 30% 60%, rgba(244, 114, 182, 0.7), transparent 45%); }
.orb-layer.b { background: radial-gradient(circle at 70% 40%, rgba(56, 189, 248, 0.6), transparent 45%); animation-duration: 12s; animation-direction: reverse; }
.orb-layer.c { background: radial-gradient(circle at 50% 80%, rgba(167, 139, 250, 0.8), transparent 40%); animation-duration: 7s; }

@keyframes swirl { to { transform: rotate(360deg); } }

/* Pipeline */
.pipe-card { min-height: 360px; }

.pipeline {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 34px;
  display: flex;
  align-items: center;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: none;
  width: 120px;
  text-align: center;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.6s, transform 0.6s var(--ease);
  transition-delay: calc(var(--i) * 110ms + 200ms);
}

.reveal.is-visible .step { opacity: 1; transform: none; }

.step-icon {
  display: grid;
  place-items: center;
  width: 60px;
  height: 60px;
  border-radius: 18px;
  background: #121215;
  border: 1px solid rgba(139, 92, 246, 0.35);
  color: #c4b5fd;
  font-size: 22px;
  box-shadow: 0 0 30px rgba(139, 92, 246, 0.15);
}

.step-label { font-size: 14px; font-weight: 500; }
.step-tool { color: var(--text-faint); font-size: 11px; }

.link {
  position: relative;
  flex: 1;
  height: 2px;
  margin-bottom: 52px;
  background: rgba(139, 92, 246, 0.2);
  overflow: hidden;
}

.pulse {
  position: absolute;
  top: 0;
  left: -30%;
  width: 30%;
  height: 100%;
  background: linear-gradient(90deg, transparent, #a78bfa, transparent);
  animation: travel 2.1s linear infinite;
}

@keyframes travel { to { left: 100%; } }

/* Waves */
.waves {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 28px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.wave-row {
  display: grid;
  grid-template-columns: 90px 1fr;
  align-items: center;
  gap: 14px;
  padding: 8px 14px;
  border-radius: 14px;
  background: #121215;
  border: 1px solid var(--line);
}

.wave-label { font-size: 13px; color: var(--text-dim); }
.wave { width: 100%; height: 40px; }
.wave path { fill: none; stroke-width: 2; vector-effect: non-scaling-stroke; stroke-linejoin: round; }
.wave.tv path { stroke: #52525b; }
.wave.mix path { stroke: #a1a1aa; }
.wave.clean path { stroke: #a78bfa; filter: drop-shadow(0 0 6px rgba(167, 139, 250, 0.7)); }

/* Latency */
.lat {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 28px;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 48px;
}

.lat-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  height: 100%;
  font-size: 14px;
}

.lat-col small { font-size: 12px; color: var(--text-faint); }
.hot { color: #c4b5fd; }

.lat-bar {
  width: 64px;
  height: 0;
  border-radius: 12px 12px 4px 4px;
  transition: height 1.4s var(--ease) 0.4s;
}

.lat-bar.before { background: #3f3f46; }
.lat-bar.after { background: linear-gradient(180deg, #c4b5fd, #7c3aed); box-shadow: 0 0 30px rgba(139, 92, 246, 0.5); }
.reveal.is-visible .lat-bar.before { height: 140px; }
.reveal.is-visible .lat-bar.after { height: 31px; }

/* Bench table */
.bench-card { min-height: 0; }

.bench {
  margin-top: 26px;
  border-radius: 16px;
  border: 1px solid var(--line);
  overflow: hidden;
}

.bench-row {
  display: grid;
  grid-template-columns: 1fr 1.4fr 110px;
  gap: 16px;
  align-items: center;
  padding: 14px 18px;
  font-size: 14px;
  background: #121215;
}

.bench-row + .bench-row { border-top: 1px solid var(--line); }
.bench-task { color: var(--text-dim); }
.bench-result { color: #c4b5fd; text-align: right; }

.bench-row.shipped {
  background: linear-gradient(90deg, rgba(139, 92, 246, 0.16), rgba(139, 92, 246, 0.04));
}

.bench-row.shipped .bench-task { color: #c4b5fd; font-weight: 500; }
.bench-row.shipped .bench-model { font-weight: 500; }

@media (max-width: 900px) {
  .pipe-card { min-height: 0; }
  .pipeline {
    position: static;
    margin-top: 28px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    row-gap: 22px;
  }
  .step { width: auto; }
  .link { display: none; }
  .aec-card, .tts-card { min-height: 420px; }
  .bench-row { grid-template-columns: 1fr auto; row-gap: 2px; }
  .bench-result { grid-column: 2; grid-row: 1 / span 2; }
  .bench-model { grid-column: 1; grid-row: 2; font-size: 13px; color: var(--text-faint); }
}
</style>
